/**
 * Clients — the registry, keyed on the thing that actually identifies the end lender.
 *
 * The one mistake this screen exists to prevent: **`credgenics` and `credgenics-genai` are not
 * the same client.** They share a prefix, an exporter and a filename convention, and they are
 * two different lenders — ICICI Bank and HDB Financial Services, on two different
 * `company_id` workspaces. Picking the wrong one mails one lender the other's borrower data,
 * and nothing downstream catches it: the file generates cleanly, SES accepts it, the run says
 * `sent`.
 *
 * So the list is built around the lender, not the slug:
 *
 *  - the end lender is a first-class column, in words, not a UUID to squint at;
 *  - `company_id` is shown as the authoritative workspace key it is (CONTRACT §2 — it is NOT
 *    derivable from the S3 prefix), including an explicit "not set" for fusion, which has none;
 *  - any family of clients that shares a slug prefix but differs on lender gets a standing
 *    banner and a per-row marker, so the collision is visible before anyone clicks.
 *
 * The registry is deliberately small and is loaded whole rather than paged: the lender check
 * is a statement about the entire set, and a page-at-a-time view cannot make it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Badge, { ActiveBadge } from '@/components/Badge';
import Button from '@/components/Button';
import { Card, PageHeader } from '@/components/Layout';
import EmptyState from '@/components/EmptyState';
import { Checkbox, Field, Input, Select } from '@/components/Field';
import Modal from '@/components/Modal';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import { callContent, isAlwaysNever } from '@/components/ColumnPolicyEditor';
import { api, errorMessage } from '@/lib/api';
import { formatCount, pluralise } from '@/lib/format';
import { MAX_LINK_TTL_DAYS, type Client, type ClientCreate } from '@/lib/types';

/** Fields the API adds beyond the wire type (services/api/clients.py :: public_client). */
interface ClientRow extends Client {
  /** True while the client exists only in config/clients/seed.json; the first save materialises it. */
  seeded_only?: boolean;
  rank_count?: number;
}

const SLUG_RE = /^[a-z0-9][a-z0-9._-]{1,63}$/;

/**
 * Clients that share a slug family, e.g. `credgenics` and `credgenics-genai`.
 * The split is on the first separator because that is exactly the confusion being guarded
 * against: a human reading `credgenics…` and stopping there.
 */
function familyKey(clientId: string): string {
  const head = clientId.split(/[-_]/, 1)[0];
  return (head ?? clientId).toLowerCase();
}

interface Family {
  key: string;
  members: ClientRow[];
  /** More than one end lender under one slug family — the dangerous case. */
  ambiguous: boolean;
}

function buildFamilies(clients: ClientRow[]): Map<string, Family> {
  const byKey = new Map<string, ClientRow[]>();
  for (const client of clients) {
    const key = familyKey(client.client_id);
    const list = byKey.get(key);
    if (list) list.push(client);
    else byKey.set(key, [client]);
  }

  const out = new Map<string, Family>();
  for (const [key, members] of byKey) {
    const lenders = new Set(
      members.map((m) => (m.company_name || m.company_id || m.client_id).trim().toLowerCase()),
    );
    out.set(key, { key, members, ambiguous: members.length > 1 && lenders.size > 1 });
  }
  return out;
}

/** Two client rows pointing at one workspace. Legal, but almost always a mistake worth naming. */
function duplicateCompanyIds(clients: ClientRow[]): Array<{ company_id: string; members: ClientRow[] }> {
  const byCompany = new Map<string, ClientRow[]>();
  for (const client of clients) {
    if (!client.company_id) continue;
    const list = byCompany.get(client.company_id);
    if (list) list.push(client);
    else byCompany.set(client.company_id, [client]);
  }
  return Array.from(byCompany.entries())
    .filter(([, members]) => members.length > 1)
    .map(([company_id, members]) => ({ company_id, members }));
}

/** Opt-in columns that carry call content, counted for the policy column. */
function contentCount(client: ClientRow): number {
  return (client.column_policy?.opt_in ?? []).filter(
    (c) => !isAlwaysNever(c) && callContent(c) !== null,
  ).length;
}

interface CreateForm {
  client_id: string;
  name: string;
  company_name: string;
  company_id: string;
  s3_prefix: string;
  link_ttl_days: number;
  active: boolean;
  no_company_id: boolean;
}

const EMPTY_CREATE: CreateForm = {
  client_id: '',
  name: '',
  company_name: '',
  company_id: '',
  s3_prefix: '',
  link_ttl_days: MAX_LINK_TTL_DAYS,
  active: true,
  no_company_id: false,
};

export default function Clients() {
  const navigate = useNavigate();
  const toast = useToast();

  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_CREATE);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const items = (await api.clients.listAll()) as ClientRow[];
      setClients(items);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const families = useMemo(() => buildFamilies(clients), [clients]);
  const ambiguousFamilies = useMemo(
    () => Array.from(families.values()).filter((f) => f.ambiguous),
    [families],
  );
  const duplicates = useMemo(() => duplicateCompanyIds(clients), [clients]);

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return clients
      .filter((client) => {
        if (activeFilter === 'active' && !client.active) return false;
        if (activeFilter === 'inactive' && client.active) return false;
        if (!needle) return true;
        return [client.client_id, client.name, client.company_name, client.company_id ?? '', client.s3_prefix]
          .join(' ')
          .toLowerCase()
          .includes(needle);
      })
      .sort((a, b) => a.client_id.localeCompare(b.client_id));
  }, [clients, filter, activeFilter]);

  // ------------------------------------------------------------------ create

  const openCreate = (): void => {
    setForm(EMPTY_CREATE);
    setCreateError(null);
    setCreateOpen(true);
  };

  const submitCreate = async (): Promise<void> => {
    const clientId = form.client_id.trim();
    if (!SLUG_RE.test(clientId)) {
      setCreateError(
        'The client id must be a lowercase slug of 2–64 characters: letters, digits, dot, dash or underscore.',
      );
      return;
    }
    if (clients.some((c) => c.client_id === clientId)) {
      setCreateError(`${clientId} already exists.`);
      return;
    }
    if (!form.company_name.trim()) {
      setCreateError('Name the end lender. It is what every guard in this console reads.');
      return;
    }
    if (!form.no_company_id && !form.company_id.trim()) {
      setCreateError(
        'Give the company_id workspace key, or tick "this client has no company_id" if it genuinely has none.',
      );
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const body: ClientCreate = {
        client_id: clientId,
        name: form.name.trim() || clientId,
        s3_prefix: form.s3_prefix.trim() || `reports/${clientId}/`,
        company_id: form.no_company_id ? null : form.company_id.trim(),
        company_name: form.company_name.trim(),
        field_map: {},
        disposition_ranks: [],
        column_policy: {},
        link_ttl_days: Math.min(Math.max(1, form.link_ttl_days), MAX_LINK_TTL_DAYS),
        active: form.active,
      };
      const created = (await api.clients.create(body)) as ClientRow;
      toast.success(`${created.client_id} created.`, {
        description: 'Set its field map, column policy and disposition ranking before scheduling it.',
      });
      setCreateOpen(false);
      navigate(`/clients/${encodeURIComponent(created.client_id)}`);
    } catch (err) {
      setCreateError(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  // ------------------------------------------------------------------ table

  const columns: Column<ClientRow>[] = [
    {
      // Rank 1. State first on every table, so it survives any overflow that gets through
      // — it used to be last, and the registry overflowed at 1440px.
      key: 'status',
      header: 'Status',
      width: '112px',
      nowrap: true,
      render: (client) => (
        <div className="cluster-tight">
          <ActiveBadge active={client.active} />
          {client.seeded_only ? (
            <Badge
              variant="plain"
              glyph="◇"
              title="Configured in config/clients/seed.json only. The first save writes the row to DynamoDB."
            >
              seed
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      // The registry is the one screen whose SUBJECT is the client record, so the id
      // leads here and the human name is the tooltip. Everywhere else it is the reverse.
      key: 'client',
      header: 'Client',
      width: '240px',
      nowrap: true,
      render: (client) => {
        const family = families.get(familyKey(client.client_id));
        const conventional = client.s3_prefix === `reports/${client.client_id}/`;
        return (
          <span className="cluster-tight" style={{ minWidth: 0 }}>
            <span
              className="mono strong truncate"
              title={`${client.client_id} · ${client.name} · s3://neuron-report/${client.s3_prefix}`}
            >
              {client.client_id}
            </span>
            {/* The prefix is `reports/{client_id}/` on every conventional client, so it is
                noise on the row until it deviates — and a deviation is worth seeing. */}
            {conventional ? null : (
              <span className="xs dim mono truncate" title={client.s3_prefix}>
                {client.s3_prefix}
              </span>
            )}
            {family?.ambiguous ? (
              <Badge
                tone="warn"
                glyph="⚠"
                title={`Shares the "${family.key}" prefix with ${family.members
                  .filter((m) => m.client_id !== client.client_id)
                  .map((m) => `${m.client_id} (${m.company_name || 'unnamed lender'})`)
                  .join(', ')}`}
              >
                shared prefix
              </Badge>
            ) : null}
          </span>
        );
      },
    },
    {
      // The one two-line cell this table is allowed, and its second line is a different
      // fact rather than a reformatting of the first: the human name, then the
      // authoritative workspace key IN FULL. It was head-truncated at 21 characters —
      // the end that discriminates least between UUIDs from one generator.
      key: 'lender',
      header: 'End lender',
      headerTitle:
        'Who the borrowers belong to. The authoritative key is company_id, which is NOT derivable from the S3 prefix (CONTRACT §2).',
      wrap: true,
      render: (client) => (
        <div className="stack-tight" style={{ minWidth: 0 }}>
          <span className="strong truncate" title={client.company_name || undefined}>
            {client.company_name || <span className="dim">— unnamed —</span>}
          </span>
          {client.company_id ? (
            <span className="xs dim mono" style={{ overflowWrap: 'anywhere' }}>
              {client.company_id}
            </span>
          ) : (
            <span className="xs">
              <Badge tone="muted" glyph="○" title="No workspace key on this client">
                company_id not set
              </Badge>
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'policy',
      header: 'Column policy',
      headerTitle: 'Opt-in columns ship only when a schedule lists them (CONTRACT §6).',
      width: '310px',
      nowrap: true,
      render: (client) => {
        const policy = client.column_policy ?? {};
        const optIn = (policy.opt_in ?? []).length;
        const content = contentCount(client);
        const open = (policy.default ?? []).length === 0;
        return (
          <div className="cluster-tight">
            <Badge
              variant="plain"
              glyph="·"
              title={open ? 'Open default tier' : 'Closed allow-list'}
            >
              {open ? 'open' : `${(policy.default ?? []).length} default`}
            </Badge>
            {/* The computed number is in the LABEL and the glyph changes, so the presence
                of call content is not a hue change alone. */}
            <Badge
              tone={content > 0 ? 'warn' : 'neutral'}
              glyph={content > 0 ? '“' : '◇'}
              title={
                content > 0
                  ? `${content} of ${optIn} opt-in columns carry call content. They ship only when a schedule lists them.`
                  : 'Opt-in columns — none of them call content'
              }
            >
              {content > 0 ? `${content} of ${optIn} opt-in` : `${formatCount(optIn)} opt-in`}
            </Badge>
            <Badge
              tone="muted"
              glyph="⊘"
              title="Columns no configuration can ever ship"
            >
              {formatCount((policy.never ?? []).length)} blocked
            </Badge>
          </div>
        );
      },
    },
    {
      // Already on every row and never rendered. A client with no ranked outcomes
      // resolves every borrower to "Not contacted", whatever happened on the call.
      key: 'ranking',
      header: 'Ranking',
      headerTitle: 'Ranked contact outcomes. With none, every borrower comes back “Not contacted”.',
      width: '96px',
      align: 'right',
      nowrap: true,
      render: (client) =>
        (client.rank_count ?? 0) > 0 ? (
          <span className="mono">{formatCount(client.rank_count ?? 0)}</span>
        ) : (
          <Badge tone="warn" glyph="○" title="No ranked outcomes on this client">
            0 ranked
          </Badge>
        ),
    },
    {
      key: 'links',
      header: 'Link life',
      headerTitle: 'Days a download link stays valid after dispatch. Hard maximum 7 (CONTRACT §1).',
      width: '88px',
      align: 'right',
      nowrap: true,
      // The detail page raises a full warn callout for exactly this condition; the list —
      // the only place all the clients can be compared — used to stay silent about it.
      render: (client) =>
        client.link_ttl_days < MAX_LINK_TTL_DAYS ? (
          <Badge
            tone="warn"
            glyph="▲"
            title={`Links for this client die after ${client.link_ttl_days} ${
              client.link_ttl_days === 1 ? 'day' : 'days'
            }, not ${MAX_LINK_TTL_DAYS}. A recipient who opens the mail the following week gets a 410 page.`}
          >
            <span className="num">{client.link_ttl_days} d</span>
          </Badge>
        ) : (
          <span className="mono" title={`Links expire ${client.link_ttl_days} days after dispatch`}>
            {client.link_ttl_days} d
          </span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Who we generate for, whose borrowers those are, and what may leave the building."
        actions={
          <>
            <Button loading={loading} onClick={() => void load()}>
              Refresh
            </Button>
            <Button variant="primary" onClick={openCreate}>
              New client
            </Button>
          </>
        }
      />

      {ambiguousFamilies.map((family) => (
        <div className="callout tone-danger" key={family.key} role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>
            <strong>
              {family.members.map((m) => m.client_id).join(' and ')} are different end lenders.
            </strong>{' '}
            {family.members
              .map((m) => `${m.client_id} → ${m.company_name || 'unnamed lender'}`)
              .join(' · ')}
            . They share the <code>{family.key}</code> prefix, the same exporter and the same
            filename convention. Read the lender column, not the slug — mailing one lender's
            report to the other's desk is not recoverable by revoking a link.
          </span>
        </div>
      ))}

      {duplicates.map((dup) => (
        <div className="callout tone-warn" key={dup.company_id}>
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>
            {dup.members.map((m) => m.client_id).join(', ')} all carry{' '}
            <code className="mono">{dup.company_id}</code> as their company_id. One workspace key on
            several clients means a report generated under either can be attributed to the same
            lender — check that this is deliberate.
          </span>
        </div>
      ))}

      <Card
        title={`Registry · ${pluralise(clients.length, 'client')}`}
        actions={
          <>
            <Input
              size="sm"
              value={filter}
              placeholder="Filter by id, lender, prefix…"
              aria-label="Filter clients"
              onChange={(event) => setFilter(event.target.value)}
              style={{ maxWidth: 220 }}
            />
            <Select
              size="sm"
              value={activeFilter}
              aria-label="Filter by status"
              onChange={(event) =>
                setActiveFilter(event.target.value as 'all' | 'active' | 'inactive')
              }
            >
              <option value="all">All</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </Select>
          </>
        }
        flush
        footer={
          <span className="xs dim">
            Showing {formatCount(rows.length)} of {formatCount(clients.length)}. The whole registry
            is loaded on purpose — the lender check above is a statement about every client, and a
            page at a time cannot make it.
          </span>
        }
      >
        <Table
          columns={columns}
          rows={rows}
          rowKey={(client) => client.client_id}
          loading={loading}
          skeletonRows={3}
          dense
          flush
          stickyHeader
          onRowClick={(client) => navigate(`/clients/${encodeURIComponent(client.client_id)}`)}
          rowTone={(client) =>
            !client.active
              ? 'muted'
              : families.get(familyKey(client.client_id))?.ambiguous
                ? 'warn'
                : 'default'
          }
          caption="Client registry"
          empty={
            loadError ? (
              <EmptyState
                tone="danger"
                title="Could not load the registry"
                description={loadError}
                action={
                  <Button variant="primary" onClick={() => void load()}>
                    Retry
                  </Button>
                }
              />
            ) : filter || activeFilter !== 'all' ? (
              <EmptyState
                tone="filtered"
                title="No client matches"
                description="Nothing in the registry matches that filter."
                action={
                  <Button
                    variant="primary"
                    onClick={() => {
                      setFilter('');
                      setActiveFilter('all');
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No clients configured"
                description="Nothing can be scheduled or dispatched until a client exists."
                action={
                  <Button variant="primary" onClick={openCreate}>
                    New client
                  </Button>
                }
              />
            )
          }
        />
      </Card>

      {/* ---------------------------------------------------------------- create */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        dismissible={!creating}
        size="lg"
        title="New client"
        description="Identity only. The field map, column policy and disposition ranking are set on the client's own page, before anything is scheduled."
        footer={
          <>
            <Button variant="ghost" disabled={creating} onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={creating} onClick={() => void submitCreate()}>
              Create client
            </Button>
          </>
        }
      >
        <div className="stack">
          <Field
            label="Client id"
            required
            hint="Lowercase slug. It is the S3 prefix, the source filename and every grant's client — it can never be changed."
          >
            {(props) => (
              <Input
                {...props}
                mono
                autoComplete="off"
                spellCheck={false}
                placeholder="credgenics-genai"
                value={form.client_id}
                onChange={(event) => {
                  const client_id = event.target.value.trim().toLowerCase();
                  setForm((f) => ({
                    ...f,
                    client_id,
                    s3_prefix:
                      f.s3_prefix && f.s3_prefix !== `reports/${f.client_id}/`
                        ? f.s3_prefix
                        : `reports/${client_id}/`,
                  }));
                  setCreateError(null);
                }}
              />
            )}
          </Field>

          <Field
            label="End lender"
            required
            hint="The company whose borrowers appear in this client's reports. Shown on every recipient and dispatch guard."
          >
            {(props) => (
              <Input
                {...props}
                placeholder="HDB Financial Services"
                value={form.company_name}
                onChange={(event) => {
                  setForm((f) => ({ ...f, company_name: event.target.value }));
                  setCreateError(null);
                }}
              />
            )}
          </Field>

          <Field
            label="company_id"
            hint="The authoritative workspace key. It is NOT derivable from the S3 prefix, so it has to be entered."
          >
            {(props) => (
              <Input
                {...props}
                mono
                autoComplete="off"
                spellCheck={false}
                disabled={form.no_company_id}
                placeholder="eb260d60-f505-4f23-8ec3-7db91d49bbda"
                value={form.no_company_id ? '' : form.company_id}
                onChange={(event) => {
                  setForm((f) => ({ ...f, company_id: event.target.value.trim() }));
                  setCreateError(null);
                }}
              />
            )}
          </Field>

          <Checkbox
            checked={form.no_company_id}
            onChange={(event) => {
              setForm((f) => ({ ...f, no_company_id: event.target.checked }));
              setCreateError(null);
            }}
            label="This client genuinely has no company_id"
            hint="Some exports carry no workspace key at all. Tick this rather than inventing one — a wrong key is worse than a missing one."
          />

          <Field label="Source prefix" hint="Read-only bucket s3://neuron-report/. Must end in a slash.">
            {(props) => (
              <Input
                {...props}
                mono
                spellCheck={false}
                placeholder="reports/credgenics-genai/"
                value={form.s3_prefix}
                onChange={(event) => setForm((f) => ({ ...f, s3_prefix: event.target.value.trim() }))}
              />
            )}
          </Field>

          <Field
            label="Link life (days)"
            hint={`How long a download link works after dispatch. Hard maximum ${MAX_LINK_TTL_DAYS} — the console clamps it, and so does the API.`}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                max={MAX_LINK_TTL_DAYS}
                value={form.link_ttl_days}
                onChange={(event) =>
                  setForm((f) => ({
                    ...f,
                    link_ttl_days: Math.min(
                      Math.max(1, Number(event.target.value) || 1),
                      MAX_LINK_TTL_DAYS,
                    ),
                  }))
                }
              />
            )}
          </Field>

          <Checkbox
            checked={form.active}
            onChange={(event) => setForm((f) => ({ ...f, active: event.target.checked }))}
            label="Active"
            hint="An inactive client is configured but dispatches nothing."
          />

          {createError ? (
            <div className="callout tone-danger" role="alert">
              <span className="callout-glyph" aria-hidden="true">
                ✕
              </span>
              <span>{createError}</span>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
